import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { ckycUpload } from "../../../api/bseApi";

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
const DOCUMENT_TYPES = [
  { value: "CKYC", label: "CKYC" },
  { value: "KYC", label: "KYC" },
  { value: "OTHER", label: "Other" },
];
const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const MAX_FILE_SIZE_MB = 5;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Convert File to Base64 string
 * @param {File} file
 * @returns {Promise<string>} Base64 string (without data URL prefix)
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = dataUrl.split(",")[1] || "";
      resolve(base64);
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

/**
 * CkycUploadScreen - BSE StAR MF CKYC Upload
 * Opens after successful UCC creation (and FATCA if applicable)
 * Receives: ClientCode, PAN, Client Name from previous screen
 * Also supports opening from User Listing via /user/ckyc/:userId
 */
export default function CkycUploadScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const [form, setForm] = useState({
    ckycNo: "",
    documentType: "CKYC",
  });
  const [context, setContext] = useState(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!userId);
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState(null);

  useEffect(() => {
    const state = location.state;

    // Flow 1: From FATCA/UCC - state has clientCode and PAN
    if (state?.clientCode && state?.PAN) {
      setContext({
        clientCode: state.clientCode,
        PAN: String(state.PAN || "").trim().toUpperCase(),
        clientName: state.clientName || "",
      });
      setFetching(false);
      return;
    }

    // Flow 2: From User Listing - userId in URL, fetch user
    if (userId) {
      const fetchUser = async () => {
        setFetching(true);
        setErrorMsg(null);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            setErrorMsg(res.data?.error || "User not found.");
            setFetching(false);
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const pan = user.pan_number ?? user.pan ?? "";
          const uccValid = ucc && String(ucc).trim() !== "" && String(ucc) !== "Reject";
          if (!uccValid || !pan) {
            setErrorMsg("Missing required data. Please complete UCC creation first.");
            setFetching(false);
            return;
          }
          const clientName = [user.first_name, user.last_name].filter(Boolean).join(" ") || "";
          setContext({
            clientCode: String(ucc).trim(),
            PAN: String(pan).trim().toUpperCase(),
            clientName,
          });
        } catch (err) {
          setErrorMsg(err.response?.data?.error || err.message || "Failed to load user.");
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }

    setErrorMsg("Missing required data. Please complete UCC and FATCA first or open from User Listing.");
    setFetching(false);
  }, [location.state, userId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    setFile(selectedFile || null);
    if (errors.documentUpload) setErrors((prev) => ({ ...prev, documentUpload: null }));
  };

  const validate = () => {
    const err = {};
    const pan = context?.PAN?.trim().toUpperCase() || "";
    if (!pan) err.PAN = "PAN is required";
    else if (!PAN_REGEX.test(pan)) err.PAN = "Invalid PAN format (e.g. ABCDE1234F)";
    if (!form.ckycNo?.trim()) err.ckycNo = "CKYC No is required";
    if (!file) err.documentUpload = "Document upload is mandatory";
    else {
      if (!ALLOWED_TYPES.includes(file.type)) {
        err.documentUpload = "Only PDF, JPG, PNG allowed";
      } else if (file.size > MAX_FILE_SIZE_BYTES) {
        err.documentUpload = `File size must be under ${MAX_FILE_SIZE_MB}MB`;
      }
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const documentContent = await fileToBase64(file);
      const payload = {
        clientCode: context.clientCode,
        PAN: context.PAN.trim().toUpperCase(),
        ckycNo: form.ckycNo.trim(),
        documentType: form.documentType,
        documentContent,
      };

      const res = await ckycUpload(payload);

      if (res.data?.status === 1) {
        const bseFailed = res.data?.bseFailed === true;
        Swal.fire({
          icon: "success",
          title: "CKYC Saved",
          text: bseFailed
            ? (res.data?.message || "CKYC details saved. BSE update failed; you can retry later.")
            : (res.data?.message || "CKYC uploaded successfully."),
        });
        navigate("/user");
      } else {
        const msg = res.data?.bseMessage || res.data?.message || "CKYC upload failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        (typeof d === "string" && d) ||
        (err.response?.status === 400 && "Invalid request. Check client code, PAN, CKYC number and document.") ||
        (err.response?.status >= 500 && "Server error. Try again or contact support.") ||
        err.message ||
        "CKYC upload failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (errorMsg) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <p className="text-danger mb-3">{errorMsg}</p>
              <Link to="/user" className="btn btn-primary">Back to Users</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link> </li>
                <li className="breadcrumb-item"><Link to="/user">UCC</Link> </li>
                <li className="breadcrumb-item active">CKYC Upload</li>
              </ol>
            </nav>
            <h5>CKYC Upload</h5>
            <p className="text-muted mb-0">
              Client: {context.clientName} | PAN: {context.PAN} | Client Code: {context.clientCode}
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                CKYC Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">PAN *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.PAN ? "is-invalid" : ""}`}
                    value={context.PAN}
                    onChange={(e) => setContext((p) => ({ ...p, PAN: e.target.value.toUpperCase() }))}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                  />
                  {errors.PAN && <div className="invalid-feedback">{errors.PAN}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">CKYC No *</label>
                  <input
                    type="text"
                    className={`form-control ${errors.ckycNo ? "is-invalid" : ""}`}
                    name="ckycNo"
                    value={form.ckycNo}
                    onChange={handleChange}
                    placeholder="Enter CKYC number"
                  />
                  {errors.ckycNo && <div className="invalid-feedback">{errors.ckycNo}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Document Type *</label>
                  <select
                    className="form-select"
                    name="documentType"
                    value={form.documentType}
                    onChange={handleChange}
                  >
                    {DOCUMENT_TYPES.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Document Upload * (PDF, JPG, PNG - max {MAX_FILE_SIZE_MB}MB)</label>
                  <input
                    type="file"
                    className={`form-control ${errors.documentUpload ? "is-invalid" : ""}`}
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                  />
                  {file && <small className="text-muted d-block mt-1">{file.name} ({(file.size / 1024).toFixed(1)} KB)</small>}
                  {errors.documentUpload && <div className="invalid-feedback">{errors.documentUpload}</div>}
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Uploading...
                    </>
                  ) : (
                    "Submit CKYC"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
